/**
 * Routes.
 *
 * `useDirection` is called once here, at the top of the tree, so `<html lang>`
 * and `<html dir>` are set before any page renders -- otherwise an Arabic-first
 * visitor would see a visible left-to-right flash on load.
 *
 * Pages are code-split: a member browsing the catalog never downloads the staff
 * dashboard.
 */

import { Suspense, lazy } from 'react'
import { Route, Routes } from 'react-router-dom'

import { AppShell } from '@/components/layout/AppShell'
import { RequireAuth, RequireStaff } from '@/components/layout/RouteGuards'
import { LoadingBlock } from '@/components/ui'
import { useDirection } from '@/hooks/useDirection'

import HomePage from './pages/HomePage'

const BrowsePage = lazy(() => import('./pages/BrowsePage'))
const BookDetailPage = lazy(() => import('./pages/BookDetailPage'))
const LibraryInfoPage = lazy(() => import('./pages/LibraryInfoPage'))
const MyReservationsPage = lazy(() => import('./pages/MyReservationsPage'))
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'))
const ManageBooksPage = lazy(() => import('./pages/admin/ManageBooksPage'))
const ManageCategoriesPage = lazy(() => import('./pages/admin/ManageCategoriesPage'))
const ManageAuthorsPage = lazy(() => import('./pages/admin/ManageAuthorsPage'))
const ManageLibraryPage = lazy(() => import('./pages/admin/ManageLibraryPage'))
const ManageMembersPage = lazy(() => import('./pages/admin/ManageMembersPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const SignInPage = lazy(() =>
  import('./pages/AuthPages').then((module) => ({ default: module.SignInPage })),
)
const SignUpPage = lazy(() =>
  import('./pages/AuthPages').then((module) => ({ default: module.SignUpPage })),
)
const ProfilePage = lazy(() =>
  import('./pages/AuthPages').then((module) => ({ default: module.ProfilePage })),
)
const ForgotPasswordPage = lazy(() =>
  import('./pages/AuthPages').then((module) => ({ default: module.ForgotPasswordPage })),
)
const SetPasswordPage = lazy(() =>
  import('./pages/AuthPages').then((module) => ({ default: module.SetPasswordPage })),
)

export default function App() {
  // Establishes lang/dir for the whole document.
  useDirection()

  return (
    <AppShell>
      <Suspense fallback={<LoadingBlock />}>
        <Routes>
          {/* Public */}
          <Route path="/" element={<HomePage />} />
          <Route path="/browse" element={<BrowsePage />} />
          <Route path="/books/:slug" element={<BookDetailPage />} />
          <Route path="/visit" element={<LibraryInfoPage />} />
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/sign-up" element={<SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          {/* The e-mailed link for sign-up, "forgot password" and staff invites. */}
          <Route path="/set-password" element={<SetPasswordPage />} />

          {/* Members */}
          <Route element={<RequireAuth />}>
            <Route path="/reservations" element={<MyReservationsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
          </Route>

          {/* Library staff */}
          <Route element={<RequireStaff />}>
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/books" element={<ManageBooksPage />} />
            <Route path="/admin/categories" element={<ManageCategoriesPage />} />
            <Route path="/admin/authors" element={<ManageAuthorsPage />} />
            <Route path="/admin/members" element={<ManageMembersPage />} />
            <Route path="/admin/library" element={<ManageLibraryPage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AppShell>
  )
}
